import { QRCODE_SCANER } from './../_interface/IQRCodeScanner';
import { filter, first, switchMap, take, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { Component, Input, OnInit, Output, ViewEncapsulation, AfterViewInit, OnDestroy, inject, Inject, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AlertService } from '../_services/alert.service';
import { GoogleLoginProvider, SocialAuthService } from '@abacritt/angularx-social-login';
import { LoginResult, RegisterResult } from '../_interface/apiResponse';
import { AuthorizationService } from '../_services/authorization.service';
import { environment } from 'src/environments/environment';
import { Location } from '@angular/common';
import { IQRCodeScaner } from '../_interface/IQRCodeScanner';
import { SessionService } from '../_services/session.service';
import { CompanyService } from '../_services/company.service';
import { ICompany } from '../_interface/company';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../_services/user.service';
import { CALLING_CODES } from '../_interface/callingCodes';
import { RecaptchaService } from '../_services/recaptcha.service';
import { isMailbox } from '../_helpers/email-format';

/** Grupna validacija: šifra i njena potvrda moraju da se poklapaju. */
const passwordsMatch = (group: AbstractControl): ValidationErrors | null => {
  const password = group.get('password')?.value;
  const confirm = group.get('confirmPassword')?.value;
  return password && confirm && password !== confirm
    ? { passwordsMismatch: true }
    : null;
};

/** Registracija traži eMail na koji može da stigne kod (domen sa tačkom). */
const mailboxValidator = (control: AbstractControl): ValidationErrors | null =>
  !control.value || isMailbox(control.value) ? null : { email: true };

@Component({
    selector: 'core-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnDestroy {
  isBusy = false;
  loginForm: UntypedFormGroup;
  environment = environment;
  destroyed = new Subject<void>();
  showLostPassword = false;
  showResetPasswordSent = false;
  /** „Zaboravljena lozinka“ je otvorena jer nalog lozinku nema (npr. otvoren u loyalty aplikaciji). */
  noPasswordAccount = false;

  /**
   * Korak koda: lozinka je tačna, ali eMail nalog nikad nije potvrdio (ili je nalog tek otvoren). Adresa na koju je kod
   * poslat — dok je postavljena, umesto forme se prikazuje unos koda. Kod se traži jednom po nalogu.
   */
  codeEmail = '';
  codeChangingEmail = false;
  /** Prevodni ključ greške na koraku koda. */
  codeError: string | null = null;
  codeSecondsLeft = 0;
  readonly codeControl = new FormControl('', { nonNullable: true });
  readonly codeNewEmailControl = new FormControl('', { nonNullable: true });
  /** Adresa i lozinka sa kojima je kod zatražen (ponovno slanje, ispravka adrese) — samo u memoriji. */
  private codeLogin = { eMail: '', password: '' };
  private codeAfterRegistration = false;
  /** Kod je tražen posle prijave preko provajdera: nema lozinke, pa ni ispravke adrese ni ponovnog slanja (nova prijava šalje nov kod). */
  codeFromProvider = false;
  private codeTimer?: number;


  private _showLogInForm = false;
  public get showLogInForm() {
    return this._showLogInForm;
  }
  @Input()
  public set showLogInForm(value) {
    this._showLogInForm = value;
  }

  invalidSession = false;

  @Output() loggedIn = new Subject<void>();

  @Input()
  showQRLogin = false;

  @Input()
  successLoginRedirect = '/';

  @Input()
  enableCodeSignin = false;

  /** Opt-in (e.g. waiter): collapse the device code (app-code-signin) behind an
   *  "eMeni ručna prijava" button instead of showing it by default. Other apps
   *  leave this false → behaviour unchanged. */
  @Input()
  manualCodeLogin = false;

  /** Whether the collapsed device code has been revealed via the button. */
  codeRevealed = false;

  /** Opt-in (waiter, KD): status veze iznad prijave (i PIN tastature) — vidi se samo dok
   *  server nije dostupan i tada zamenjuje globalni "Niste povezani na internet" modal.
   *  Ostali app-ovi ostavljaju false → bez promene. */
  @Input()
  showConnectionStatus = false;

  @Input()
  enableGoogleSignin = true;

  @Input()
  enableFacebookSignin = true;

  @Input()
  enableUsernamePasswordSignin = true;

  @Input()
  showPINLogin = false;

  /** Opt-in (backoffice): shows the "create account" entry + registration form.
   *  Other apps leave this false → no visual change. */
  @Input()
  showRegistration = false;

  /** Route to open after a successful registration (auto-login) — e.g. the
   *  backoffice "new company" page. */
  @Input()
  registrationRedirect = '/';

  /** True while the registration form is the active mode. */
  showRegisterForm = false;

  showRegisterPassword = false;

  registerForm: UntypedFormGroup;

  readonly callingCodes = CALLING_CODES;

  get showLoginButtons() {
    return this.enableGoogleSignin || this.enableFacebookSignin || this.enableUsernamePasswordSignin;
  }

  /** True when a sign-in method is shown above the method list (username form /
   *  lost-password / device code). Then the separator reads "Drugi načini prijave";
   *  otherwise the list itself IS the primary content → "Prijavite se". */
  get anyMethodActive(): boolean {
    return this.showLogInForm || this.showLostPassword
      || (this.manualCodeLogin ? this.codeRevealed : this.enableCodeSignin);
  }

  /** True while the PIN keypad is showing (vs the main login screen). */
  get isPinLoginActive(): boolean {
    return !!this.pinConfig && this.showPINLogin;
  }

  handleToken() {

  }
  constructor(private authService: SocialAuthService,
    fb: UntypedFormBuilder,
    public myAuthService: AuthorizationService,
    private router: Router,
    private alert: AlertService,
    private userService: UserService,
    public session: SessionService,
    private translate: TranslateService,
    private recaptcha: RecaptchaService,
    private cs: CompanyService,
    location: Location,
    activatedRoute: ActivatedRoute,
    @Inject(QRCODE_SCANER) private qrCodeScaner: IQRCodeScaner
  ) {

    this.invalidSession = (location.getState() as any)?.['invalidSession'];

    this.authService.authState.pipe(
      takeUntil(this.destroyed)
    ).subscribe((user) => {
      if (!user) {
        return;
      }

      switch (user.provider) {
        case ('GOOGLE'):
          this.sendGoogleAuthToken(user.idToken);
          break;
      }
    });

    activatedRoute.params.pipe(
      takeUntil(this.destroyed)
    ).subscribe(
      params => {
        var token = params["token"];
        if (token) {
          myAuthService.loginWithToken(token).subscribe(
            result => {
              this.loginCompleted(result, true); // silent: URL-token auto-login on open
            }
          )
        }
      }
    )

    this.loginForm = fb.group({
      'username': '',
      'password': ''
    });

    this.registerForm = fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      eMail: ['', [Validators.required, Validators.email, mailboxValidator]],
      // bar 8 karaktera, bar jedan ne-broj i bar jedan broj
      password: ['', [Validators.required, Validators.minLength(8), Validators.pattern(/^(?=.*\D)(?=.*\d).*$/)]],
      confirmPassword: ['', Validators.required],
      phoneCode: ['+381'],
      phone: ['', [Validators.required, Validators.pattern(/^[0-9][0-9 \/-]{4,14}$/)]],
      acceptTerms: [false, Validators.requiredTrue],
    }, { validators: passwordsMatch });

  }
  ngOnDestroy(): void {
    clearInterval(this.codeTimer);
    this.destroyed.next();
    this.destroyed.complete();
  }


  sendGoogleAuthToken(token: string) {
    this.myAuthService.googleSignInExternal(token)
      // .pipe(finalize(() => this.isLoading = false))
      .subscribe(result => {
        this.loginCompleted(result, true); // silent: restored Google session on open
      });
  }

  signInWithCode() {
    var previousData = '';

    this.qrCodeScaner.start().subscribe(
      data => {
        if (previousData == data) {
          return;
        }
        previousData = data;
        this.qrCodeScaner.disable();
        // Let the user know the scan registered and we're now waiting on the server.
        this.qrCodeScaner.setBusy(true);

        this.myAuthService.loginWithToken(data).subscribe({
          next: result => {
            this.qrCodeScaner.setBusy(false);

            if (result == LoginResult.OK) {
              this.qrCodeScaner.stop();
            }

            this.qrCodeScaner.enable();

            this.loginCompleted(result);
          },
          // On a network/server error: clear the overlay and re-arm the scanner so
          // the user can retry instead of staring at a frozen busy spinner.
          error: () => {
            this.qrCodeScaner.setBusy(false);
            this.qrCodeScaner.enable();
            previousData = '';
          }
        })
      }
    );
  }

  logout(): void {
    this.myAuthService.signOut();
  }


  login() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isBusy = true;

    this.myAuthService.login(this.loginForm.value).subscribe({
      next: result => {
        if (result === LoginResult.EmailCodeSent) {
          this.isBusy = false;
          this.startCodeStep(this.loginForm.value.username.trim(), this.loginForm.value.password, false);
          return;
        }

        if (result === LoginResult.NoPassword) {
          this.isBusy = false;
          this.noPasswordAccount = true;
          this.showLostPassword = true;
          return;
        }

        this.loginCompleted(result);
      },
      // Bez ove grane HTTP greška (server nedostupan) ostavlja formu zauvek
      // zaključanu — korisnik ne može ni da ponovi pokušaj.
      error: () => {
        this.isBusy = false;
        this.alert.error(this.translate.instant('loginScreen.connectionError'));
      }
    });
  }

  loginCompleted(result: LoginResult, silent = false) {
    this.isBusy = false;
    switch (result) {
      case (LoginResult.OK):
        this.loggedIn.next();

        this.router.navigate([this.successLoginRedirect]);

        this.setCompanyForPINLogin();
        break;
      // A silent auto-login (restored Google session / URL token on app open) that the
      // backend rejects must NOT alert — just stay on the login screen. Only an explicit
      // login attempt surfaces the reason (as a dialog; the info-bar isn't on this screen).
      case (LoginResult.Disabled):
        if (!silent) this.alert.error(this.translate.instant('accountLocked'));
        break;
      case (LoginResult.AccessDenied):
        if (!silent) this.alert.error(this.translate.instant('accessDenied'));
        break;
      case (LoginResult.InvalidToken):
        if (!silent) this.alert.error(this.translate.instant('invalidToken'));
        break;
      case (LoginResult.InvalidUserName):
        if (!silent) this.alert.error(this.translate.instant('invalidUsernameOrPassword'));
        break;
      case (LoginResult.PersonalCredentialRequired):
        if (!silent) this.alert.error(this.translate.instant('personalCredentialRequired'));
        break;
      case (LoginResult.InvalidPin):
        if (!silent) this.alert.error(this.translate.instant('pinLogin.invalidPin'));
        break;
      case (LoginResult.PinDeviceExpired):
        if (!silent) this.alert.error(
          this.translate.instant('pinLogin.deviceExpiredTitle'),
          this.translate.instant('pinLogin.deviceExpiredText')
        );
        break;
      case (LoginResult.TooManyRequests):
        if (!silent) this.alert.error(this.translate.instant('loginScreen.codeTooMany'));
        break;
      case (LoginResult.EmailCodeSent):
        // prijava preko provajdera stala na potvrdi eMail-a naloga
        this.startCodeStep(this.myAuthService.providerCodeEmail, '', false);
        this.codeFromProvider = true;
        break;
    }
  }

  private startCodeStep(eMail: string, password: string, afterRegistration: boolean) {
    this.codeLogin = { eMail, password };
    this.codeAfterRegistration = afterRegistration;
    this.showRegisterForm = false;
    this.setCodeEmail(eMail);
  }

  private setCodeEmail(eMail: string) {
    this.codeEmail = eMail;
    this.codeChangingEmail = false;
    this.codeError = null;
    this.codeControl.reset();
    this.startCodeTimer();
  }

  /** Server šalje nov kod najranije posle 60 s. */
  private startCodeTimer() {
    clearInterval(this.codeTimer);
    this.codeSecondsLeft = 60;
    this.codeTimer = window.setInterval(() => {
      this.codeSecondsLeft--;
      if (this.codeSecondsLeft <= 0) {
        clearInterval(this.codeTimer);
      }
    }, 1000);
  }

  verifyCode() {
    const code = this.codeControl.value.trim();
    if (!/^\d{6}$/.test(code)) {
      this.codeError = 'loginScreen.codeFormat';
      return;
    }

    this.isBusy = true;
    this.codeError = null;

    const request = this.codeAfterRegistration
      ? this.myAuthService.confirmSignUp(this.codeEmail, code)
      : this.myAuthService.verifySignIn(this.codeEmail, code);

    request.subscribe({
      next: result => {
        this.isBusy = false;
        if (result !== LoginResult.OK) {
          this.codeError = this.codeErrorKey(result);
          return;
        }

        const afterRegistration = this.codeAfterRegistration;
        this.clearCodeStep();

        if (afterRegistration) {
          this.alert.success(
            this.translate.instant('register.successTitle'),
            this.translate.instant('register.successText')
          );
          // Namerno BEZ loggedIn.next() — vidi register(): ruta za dodavanje firme pobeđuje.
          this.router.navigate([this.registrationRedirect]);
          return;
        }

        this.loginCompleted(LoginResult.OK);
      },
      error: () => {
        this.isBusy = false;
        this.codeError = 'loginScreen.connectionError';
      }
    });
  }

  /** Ponovno slanje: za prvobitnu adresu ponovo prijava (server šalje nov kod), za ispravljenu ponovo promena. */
  resendCode() {
    if (this.codeAfterRegistration) {
      this.signUpAgain(this.codeEmail, () => this.startCodeTimer());
      return;
    }

    const request = this.codeEmail === this.codeLogin.eMail
      ? this.myAuthService.login({ username: this.codeLogin.eMail, password: this.codeLogin.password })
      : this.myAuthService.changeSignInEmail(this.codeLogin.eMail, this.codeLogin.password, this.codeEmail);

    this.codeRequest(request, () => this.startCodeTimer());
  }

  startCodeEmailChange() {
    this.codeNewEmailControl.reset();
    this.codeError = null;
    this.codeChangingEmail = true;
  }

  changeCodeEmail() {
    const newEmail = this.codeNewEmailControl.value.trim();
    if (!isMailbox(newEmail)) {
      this.codeError = 'loginScreen.codeInvalidEmail';
      return;
    }

    if (this.codeAfterRegistration) {
      this.signUpAgain(newEmail, () => {
        this.registerForm.patchValue({ eMail: newEmail });
        this.setCodeEmail(newEmail);
      });
      return;
    }

    this.codeRequest(
      this.myAuthService.changeSignInEmail(this.codeLogin.eMail, this.codeLogin.password, newEmail),
      () => this.setCodeEmail(newEmail));
  }

  /** Registracija čeka kod (nalog još ne postoji): ponovno slanje i ispravka adrese su nova registracija sa istim podacima. */
  private async signUpAgain(eMail: string, onSent: () => void) {
    this.isBusy = true;
    this.codeError = null;

    const recaptchaToken = await this.recaptcha.execute('register');
    const v = this.registerForm.value;

    this.myAuthService.register({
      firstName: v.firstName,
      lastName: v.lastName,
      eMail,
      password: v.password,
      phone: `${v.phoneCode} ${v.phone}`.trim(),
      recaptchaToken,
    }).subscribe({
      next: result => {
        this.isBusy = false;
        switch (result) {
          case RegisterResult.CodeSent:
            onSent();
            break;
          case RegisterResult.MailAlreadyRegistered:
            this.codeError = 'loginScreen.codeEmailTaken';
            break;
          case RegisterResult.InvalidData:
            this.codeError = 'loginScreen.codeInvalidEmail';
            break;
          case RegisterResult.TooManyRequests:
            this.codeError = 'loginScreen.codeTooMany';
            break;
          default:
            this.codeError = 'loginScreen.codeFailed';
            break;
        }
      },
      error: () => {
        this.isBusy = false;
        this.codeError = 'loginScreen.connectionError';
      }
    });
  }

  cancelCode() {
    this.clearCodeStep();
  }

  private codeRequest(request: Observable<LoginResult>, onSent: () => void) {
    this.isBusy = true;
    this.codeError = null;

    request.subscribe({
      next: result => {
        this.isBusy = false;
        if (result === LoginResult.EmailCodeSent) {
          onSent();
          return;
        }

        if (result === LoginResult.OK) {
          this.clearCodeStep();
          this.loginCompleted(LoginResult.OK);
          return;
        }

        this.codeError = this.codeErrorKey(result);
      },
      error: () => {
        this.isBusy = false;
        this.codeError = 'loginScreen.connectionError';
      }
    });
  }

  private clearCodeStep() {
    clearInterval(this.codeTimer);
    this.codeEmail = '';
    this.codeChangingEmail = false;
    this.codeError = null;
    this.codeLogin = { eMail: '', password: '' };
    this.codeAfterRegistration = false;
    this.codeFromProvider = false;
  }

  private codeErrorKey(result: LoginResult): string {
    switch (result) {
      case LoginResult.InvalidCode:
        return 'loginScreen.codeInvalid';
      case LoginResult.TooManyRequests:
        return 'loginScreen.codeTooMany';
      case LoginResult.EmailTaken:
        return 'loginScreen.codeEmailTaken';
      case LoginResult.InvalidEmail:
        return 'loginScreen.codeInvalidEmail';
      case LoginResult.InvalidUserName:
        return 'invalidUsernameOrPassword';
      case LoginResult.Disabled:
        return 'accountLocked';
      default:
        return 'loginScreen.codeFailed';
    }
  }

  setCompanyForPINLogin() {
    var c: ICompany;
    this.cs.getUserCompanies().pipe(
      filter(companies => companies.length == 1),
      switchMap(company => {
        c = company[0];
        return this.cs.getSettings(c.companyID)
      })
    ).subscribe({
      next: settings => {
        if (settings) {
          if (settings.pinToken == null) {
            this.pinConfig = null;
          }
          else {
            var pinConfig = {
              companyID: c.companyID,
              token: settings.pinToken,
              companyName: c.name,
            }

            localStorage.setItem('pinConfig', JSON.stringify(pinConfig));
          }
        }
      }
    });
  }

  pinLogout() {
    this.pinConfig = null;
  }

  set pinConfig(config: any) {
    if (!config) {
      localStorage.removeItem('pinConfig');
    }
    else {
      localStorage.setItem('pinConfig', JSON.stringify(config));
    }
  }

  get pinConfig(): any {
    var config = localStorage.getItem('pinConfig');
    if (!config) {
      return null;
    }
    var jConfig = JSON.parse(config);
    if (jConfig.token) {
      return jConfig;
    }

    return null;
  }

  pinLoginCompleted(result: LoginResult) {
    // pin-login emituje ishod prijave, ne token — ranije se ovde uvek slalo OK,
    // pa se razlog neuspele PIN prijave nije video.
    this.loginCompleted(result);
  }

  codeLoginCompleted(jwt: string) {
    this.myAuthService.loginCompleted(jwt);
    this.loginCompleted(LoginResult.OK);
  }

  openRegistration() {
    this.showRegisterForm = true;
    this.showLostPassword = false;
    this.showResetPasswordSent = false;

    // Skripta se povlači tek ovde (ne pri startu aplikacije) — aplikacije bez
    // registracije nikad ne kontaktiraju Google. Rezultat ne čekamo: token se
    // traži pri submitu, do tada je skripta po pravilu već spremna.
    this.recaptcha.execute('register_open');
  }

  closeRegistration() {
    this.showRegisterForm = false;
  }

  async register() {
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }

    this.isBusy = true;

    // Nikad ne baca: bez konfigurisane reCAPTCHA (ili bez interneta) vraća null,
    // pa server odlučuje da li prazan token prolazi.
    const recaptchaToken = await this.recaptcha.execute('register');

    const v = this.registerForm.value;
    this.myAuthService.register({
      firstName: v.firstName,
      lastName: v.lastName,
      eMail: v.eMail,
      password: v.password,
      phone: `${v.phoneCode} ${v.phone}`.trim(),
      recaptchaToken,
    }).subscribe({
      next: result => {
        this.isBusy = false;

        switch (result) {
          case RegisterResult.CodeSent:
            this.startCodeStep(v.eMail.trim(), v.password, true);
            break;

          case RegisterResult.OK:
            this.alert.success(
              this.translate.instant('register.successTitle'),
              this.translate.instant('register.successText')
            );
            // register() je već napravio sesiju (loginCompleted), pa ova ruta zaista
            // otvara dodavanje firme. Namerno BEZ loggedIn.next() — aplikacija na
            // currentUser ionako reaguje sopstvenom navigacijom, a ovo je poslednja
            // zatražena ruta i ona pobeđuje.
            this.router.navigate([this.registrationRedirect]);
            break;

          case RegisterResult.MailAlreadyRegistered:
            this.registerForm.get('eMail')!.setErrors({ taken: true });
            this.alert.error(this.translate.instant('register.mailTaken'));
            break;

          case RegisterResult.InvalidData:
            this.alert.error(this.translate.instant('register.invalidData'));
            break;

          case RegisterResult.CaptchaFailed:
            this.alert.error(this.translate.instant('register.captchaFailed'));
            break;

          case RegisterResult.TooManyRequests:
            this.alert.error(this.translate.instant('loginScreen.codeTooMany'));
            break;

          default:
            this.alert.error(this.translate.instant('register.failed'));
            break;
        }
      },
      error: () => {
        this.isBusy = false;
        this.alert.error(this.translate.instant('register.failed'));
      },
    });
  }

  sendResetPasswordLink(eMail: string) {
    this.userService.sendResetPasswordLink(eMail).subscribe(() => {
      this.showResetPasswordSent = true;
    });
  }


  signInWithGoogle(): void {
    this.createFakeGoogleWrapper().click();

/*    this.authService.signIn(GoogleLoginProvider.PROVIDER_ID)
      .then((userData) => {
        // on success
        this.isBusy = true;
        // this will return user data from google. What you need is a user token which you will send it to the server
      });
      */
  }


  createFakeGoogleWrapper = () => {
    const googleLoginWrapper = document.createElement('div');
    googleLoginWrapper.style.display = 'none';
    googleLoginWrapper.classList.add('custom-google-button');
    document.body.appendChild(googleLoginWrapper);
    window.google.accounts.id.renderButton(googleLoginWrapper, {
      type: 'icon',
      width: '200',
    });

    const googleLoginWrapperButton = googleLoginWrapper.querySelector(
      'div[role=button]'
    ) as HTMLElement;

    return {
      click: () => {
        googleLoginWrapperButton?.click();
      },
    };
  };

}
